// ─────────────────────────────────────────────
// Oike Pricing Engine — V1
// All prices are base prices for a 1-2 person commercial team.
// To change a price, edit PRICING_CONFIG only.
// ─────────────────────────────────────────────

export const PRICING_CONFIG = {
  countries: {
    usa:       { label: 'United States',   currency: 'USD', symbol: '$',    lang: 'en', diagnostic: 1800,   systemDesign: 4000,    enablement: 2500,    advisory: 1800  },
    canada:    { label: 'Canada',          currency: 'USD', symbol: '$',    lang: 'en', diagnostic: 1700,   systemDesign: 3800,    enablement: 2400,    advisory: 1700  },
    uk:        { label: 'United Kingdom',  currency: 'GBP', symbol: '£',   lang: 'en', diagnostic: 1200,   systemDesign: 2800,    enablement: 1800,    advisory: 1400  },
    spain:     { label: 'Spain',           currency: 'EUR', symbol: '€',   lang: 'es', diagnostic: 1200,   systemDesign: 2800,    enablement: 1800,    advisory: 1300  },
    mexico:    { label: 'Mexico',          currency: 'MXN', symbol: 'MXN ', lang: 'es', diagnostic: 24000,  systemDesign: 55000,   enablement: 35000,   advisory: 24000 },
    chile:     { label: 'Chile',           currency: 'CLP', symbol: 'CLP ', lang: 'es', diagnostic: 650000, systemDesign: 1800000, enablement: 1100000, advisory: 750000 },
    uruguay:   { label: 'Uruguay',         currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 1200,   systemDesign: 2800,    enablement: 1800,    advisory: 1100  },
    argentina: { label: 'Argentina',       currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 900,    systemDesign: 2200,    enablement: 1400,    advisory: 850   },
    bolivia:   { label: 'Bolivia',         currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 750,    systemDesign: 1800,    enablement: 1100,    advisory: 700   },
    colombia:  { label: 'Colombia',        currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 900,    systemDesign: 2200,    enablement: 1400,    advisory: 850   },
    peru:      { label: 'Peru',            currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 900,    systemDesign: 2200,    enablement: 1400,    advisory: 850   },
    ecuador:   { label: 'Ecuador',         currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 750,    systemDesign: 1800,    enablement: 1100,    advisory: 700   },
    paraguay:  { label: 'Paraguay',        currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 750,    systemDesign: 1800,    enablement: 1100,    advisory: 700   },
    brazil:    { label: 'Brazil',          currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 1200,   systemDesign: 2800,    enablement: 1800,    advisory: 1100  },
    venezuela: { label: 'Venezuela',       currency: 'USD', symbol: '$',    lang: 'es', diagnostic: 750,    systemDesign: 1800,    enablement: 1100,    advisory: 700   },
  },

  // Per-service multipliers by commercial team size.
  // 30+ always returns null (custom pricing).
  teamMultipliers: {
    diagnostic:   { '1-2': 1.00, '3-10': 1.15, '11-30': 1.35, '30+': null },
    systemDesign: { '1-2': 1.00, '3-10': 1.20, '11-30': 1.50, '30+': null },
    enablement:   { '1-2': 1.00, '3-10': 1.40, '11-30': 1.90, '30+': null },
    advisory:     { '1-2': 1.00, '3-10': 1.15, '11-30': 1.40, '30+': null },
  },

  services: {
    diagnostic:   { label: 'Oike Diagnostic',     isMonthly: false },
    systemDesign: { label: 'Oike System Design',  isMonthly: false },
    enablement:   { label: 'Oike Enablement',     isMonthly: false },
    advisory:     { label: 'Oike Advisory',        isMonthly: true  },
  },
};

// Team size bucket from raw number
export function teamSizeBucket(n) {
  if (n == null || isNaN(n)) return null;
  if (n <= 2)  return '1-2';
  if (n <= 10) return '3-10';
  if (n <= 30) return '11-30';
  return '30+';
}

// Round to clean commercial figures per currency
function round(price, currency) {
  if (['CLP'].includes(currency)) return Math.round(price / 50000) * 50000;
  if (['MXN'].includes(currency)) return Math.round(price / 500) * 500;
  return Math.round(price / 50) * 50;
}

/**
 * Calculate price for one service.
 * Returns { price, currency, symbol, isCustom, label, isMonthly }
 */
export function calculatePrice(countryKey, serviceKey, teamNumber) {
  const country = PRICING_CONFIG.countries[countryKey];
  const service = PRICING_CONFIG.services[serviceKey];
  const multipliers = PRICING_CONFIG.teamMultipliers[serviceKey];
  if (!country || !service || !multipliers) return null;

  const bucket = teamSizeBucket(teamNumber);
  if (!bucket) return null;
  if (bucket === '30+') {
    return { isCustom: true, label: service.label, currency: country.currency, symbol: country.symbol, isMonthly: service.isMonthly };
  }

  const base = country[serviceKey];
  const multiplier = multipliers[bucket];
  const raw = base * multiplier;
  const price = round(raw, country.currency);

  return {
    price,
    currency: country.currency,
    symbol: country.symbol,
    label: service.label,
    isMonthly: service.isMonthly,
    isCustom: false,
    bucket,
    multiplier,
    base,
  };
}

/**
 * Format a price result for display.
 * e.g. "From $4,800" or "From MXN 66,500 /mo"
 */
export function formatPrice(result, lang = 'en') {
  if (!result) return '—';
  if (result.isCustom) return lang === 'es' ? 'Precio personalizado' : 'Custom pricing';
  const num = result.price.toLocaleString('en-US');
  const suffix = result.isMonthly ? (lang === 'es' ? ' /mes' : ' /mo') : '';
  const from = lang === 'es' ? 'Desde ' : 'From ';
  return `${from}${result.symbol}${num}${suffix}`;
}

// ─────────────────────────────────────────────
// Heuristic extractors (used as fallback when AI is unavailable)
// ─────────────────────────────────────────────

// Normalize: decompose accented chars so keywords without tildes also match
function normalizeText(str) {
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const COUNTRY_KEYWORDS = {
  usa:       ['usa', 'united states', 'u\\.s\\.a\\.?', 'estados unidos', 'new york', 'california', 'florida', 'texas'],
  canada:    ['canada', 'toronto', 'montreal', 'vancouver'],
  uk:        ['uk', 'united kingdom', 'england', 'britain', 'london', 'reino unido'],
  spain:     ['spain', 'espana', 'madrid', 'barcelona'],
  mexico:    ['mexico', 'cdmx', 'monterrey', 'guadalajara'],
  chile:     ['chile', 'santiago de chile'],
  uruguay:   ['uruguay', 'montevideo'],
  argentina: ['argentina', 'buenos aires', 'cordoba', 'rosario'],
  bolivia:   ['bolivia', 'la paz', 'santa cruz', 'cochabamba'],
  colombia:  ['colombia', 'bogota', 'medellin', 'cali', 'cartagena'],
  peru:      ['peru', 'lima', 'arequipa', 'cusco'],
  ecuador:   ['ecuador', 'quito', 'guayaquil', 'cuenca'],
  paraguay:  ['paraguay', 'asuncion'],
  brazil:    ['brazil', 'brasil', 'sao paulo', 'rio de janeiro', 'brasilia', 'belo horizonte'],
  venezuela: ['venezuela', 'caracas', 'maracaibo', 'valencia'],
};

export function detectCountry(text) {
  if (!text) return null;
  const norm = normalizeText(text);
  const counts = {};
  for (const [key, keywords] of Object.entries(COUNTRY_KEYWORDS)) {
    let hits = 0;
    for (const kw of keywords) {
      // Use word boundaries; escape dots for plain keywords
      const escaped = kw.replace(/\./g, '\\.').replace(/\s+/g, '\\s+');
      const re = new RegExp(`\\b${escaped}\\b`, 'i');
      if (re.test(norm)) hits++;
    }
    if (hits > 0) counts[key] = hits;
  }
  if (Object.keys(counts).length === 0) return null;
  // Return country with most keyword matches
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

export function detectTeamSize(text) {
  if (!text) return null;
  // Look for patterns like "5 sellers", "3 SDRs", "team of 8", "2 vendedores", etc.
  const patterns = [
    /team\s+of\s+(\d+)/i,
    /(\d+)\s*(sales|seller|vendedor|sdr|bdr|ae|account\s+exec|commercial|comercial)/i,
    /(\d+)\s*persona/i,
    /(\d+)\s*people/i,
    /founder\s*\+\s*(\d+)/i,
    /(\d+)\s*\+\s*founder/i,
    /^(\d+)$/,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const n = parseInt(match[1], 10);
      if (n > 0 && n < 200) return n;
    }
  }
  // No fallback generic (\d+) — return null so user fills it manually
  return null;
}

// Language for quote: driven by country config
export function quoteLangForCountry(countryKey) {
  return PRICING_CONFIG.countries[countryKey]?.lang || 'es';
}

export const COUNTRY_LIST = Object.entries(PRICING_CONFIG.countries).map(([key, v]) => ({ key, label: v.label }));
export const SERVICE_LIST = Object.entries(PRICING_CONFIG.services).map(([key, v]) => ({ key, label: v.label }));
